import React from 'react';
import Header from '../components/Header';
import FooterMinimal from '../components/FooterMinimal';
import { useLanguage, useLocalizedNavigate, localizePath } from '../contexts/LanguageContext';
import events from '../data/events';
import seo from '../seo/pages.json';

const textStyle = {
  fontSize: '1rem',
  color: '#a3a3a3',
  lineHeight: 1.8,
  fontWeight: 300
};

const sectionTitleStyle = {
  fontSize: '1.5rem',
  fontWeight: 400,
  marginBottom: '1.5rem',
  color: '#fff'
};

const List = ({ items }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '1rem' }}>
    {items.map((item) => (
      <li key={item} style={{ ...textStyle, paddingLeft: '1.5rem', position: 'relative' }}>
        <span style={{ position: 'absolute', left: 0, color: '#525252' }}>—</span>
        {item}
      </li>
    ))}
  </ul>
);

// Page d'un événement ou d'une destination (Grand Prix, Cannes, Saint-Tropez…), en français ou en anglais.
const EventPage = ({ event }) => {
  const navigate = useLocalizedNavigate();
  const { language } = useLanguage();
  const content = event[language];
  const others = events.filter((other) => other.slug !== event.slug);

  const goToContact = () => {
    navigate('/');
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        name: content.title,
        description: content.intro,
        serviceType: language === 'fr' ? 'Protection rapprochée' : 'Close protection',
        provider: { '@id': seo.organization['@id'] },
        url: `${seo.siteUrl}${localizePath(event.path, language)}`
      },
      {
        '@type': 'FAQPage',
        mainEntity: content.faq.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a }
        }))
      }
    ]
  };

  return (
    <div className="App">
      <Header onScrollToContact={goToContact} />
      <main style={{ background: '#000', minHeight: '100vh', paddingTop: '80px' }}>
        <section style={{ padding: '8rem 0 4rem', borderBottom: '1px solid #1a1a1a' }}>
          <div className="container-custom">
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'transparent',
                border: '1px solid #262626',
                color: '#a3a3a3',
                padding: '0.75rem 1.5rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginBottom: '3rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#404040';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#262626';
                e.currentTarget.style.color = '#a3a3a3';
              }}
            >
              ← {language === 'fr' ? 'Retour' : 'Back'}
            </button>

            <div style={{ fontSize: '0.8rem', letterSpacing: '3px', color: '#525252', marginBottom: '1.5rem', fontWeight: 500, textTransform: 'uppercase' }}>
              {content.label} · {content.period}
            </div>

            <h1 className="event-title" style={{ fontSize: '4rem', fontWeight: 300, marginBottom: '2.5rem', letterSpacing: '-0.02em', color: '#fff', lineHeight: 1.1 }}>
              {content.title}
            </h1>

            <p style={{ fontSize: '1.15rem', color: '#a3a3a3', lineHeight: 1.8, maxWidth: '900px', fontWeight: 300 }}>
              {content.intro}
            </p>

            {content.note && (
              <p style={{ fontSize: '0.8rem', color: '#525252', marginTop: '1.5rem', maxWidth: '900px' }}>
                {content.note}
              </p>
            )}
          </div>
        </section>

        <section style={{ padding: '6rem 0', borderBottom: '1px solid #1a1a1a' }}>
          <div className="container-custom">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '4rem', maxWidth: '1200px' }}>
              <div>
                <h2 style={sectionTitleStyle}>{content.challengesTitle}</h2>
                <List items={content.challenges} />
              </div>
              <div>
                <h2 style={sectionTitleStyle}>{content.approachTitle}</h2>
                <List items={content.approach} />
              </div>
            </div>
          </div>
        </section>

        <section style={{ padding: '6rem 0', borderBottom: '1px solid #1a1a1a' }}>
          <div className="container-custom" style={{ maxWidth: '900px' }}>
            <h2 style={{ ...sectionTitleStyle, marginBottom: '2.5rem' }}>
              {language === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
            </h2>
            <div style={{ display: 'grid', gap: '2.5rem' }}>
              {content.faq.map(({ q, a }) => (
                <div key={q}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 500, color: '#fff', marginBottom: '0.75rem' }}>{q}</h3>
                  <p style={textStyle}>{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={{ padding: '6rem 0', borderBottom: '1px solid #1a1a1a', textAlign: 'center' }}>
          <div className="container-custom">
            <h2 style={{ fontSize: '2.2rem', fontWeight: 300, color: '#fff', marginBottom: '1rem' }}>
              {language === 'fr' ? 'Préparons votre dispositif' : 'Let’s plan your protection'}
            </h2>
            <p style={{ ...textStyle, marginBottom: '2.5rem' }}>
              {language === 'fr'
                ? 'Réponse confidentielle sous 24 heures.'
                : 'Confidential reply within 24 hours.'}
            </p>
            <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={goToContact} className="hero-cta" style={{ cursor: 'pointer', background: 'transparent' }}>
                {language === 'fr' ? "Demande d'information" : 'Request information'}
              </button>
              <a href="tel:+33180889587" className="hero-cta">+33 1 80 88 95 87</a>
            </div>
          </div>
        </section>

        <section style={{ padding: '5rem 0' }}>
          <div className="container-custom">
            <h2 style={{ fontSize: '0.8rem', letterSpacing: '3px', color: '#525252', marginBottom: '2rem', fontWeight: 500, textTransform: 'uppercase' }}>
              {language === 'fr' ? 'Autres événements et destinations' : 'Other events and destinations'}
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              {others.map((other) => (
                <a
                  key={other.slug}
                  href={localizePath(other.path, language)}
                  onClick={(e) => { e.preventDefault(); navigate(other.path); }}
                  style={{ color: '#a3a3a3', border: '1px solid #262626', borderRadius: '4px', padding: '0.6rem 1.1rem', fontSize: '0.9rem', textDecoration: 'none' }}
                >
                  {other[language].name}
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <FooterMinimal />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
    </div>
  );
};

export default EventPage;
